const SEEK_DRIFT_S = 0.25;
const NUDGE_DRIFT_S = 0.04;
const STRONG_DRIFT_S = 0.12;
const NUDGE = 0.03;
const STRONG_NUDGE = 0.05;
const MIN_MEDIA_RATE = 0.0625;
const MAX_MEDIA_RATE = 16;

export type Correction = { readonly kind: 'rate'; readonly rate: number } | { readonly kind: 'seek'; readonly time: number };

export function correctDrift(desired: number, actual: number, rate: number): Correction {
  const tolerance = Math.max(1, rate / 2);
  const drift = actual - desired;
  const size = Math.abs(drift);
  if (size >= SEEK_DRIFT_S * tolerance) return { kind: 'seek', time: desired };
  if (size < NUDGE_DRIFT_S * tolerance) return { kind: 'rate', rate };
  const nudge = size >= STRONG_DRIFT_S * tolerance ? STRONG_NUDGE : NUDGE;
  return { kind: 'rate', rate: Math.min(MAX_MEDIA_RATE, Math.max(MIN_MEDIA_RATE, rate * (1 - Math.sign(drift) * nudge))) };
}

const HAVE_FUTURE_DATA = 3;
const CLIP_TAIL_S = 0.15;
const MAX_STALL_MS = 1500;

export interface CameraState {
  readonly loaded: boolean;
  readonly readyState: number;
  readonly seeking: boolean;
  readonly ended: boolean;
  readonly failed: boolean;
  readonly mediaTime: number;
  readonly span: number;
}

export function isBlocking(camera: CameraState): boolean {
  if (camera.failed || camera.ended) return false;
  if (!camera.loaded || camera.seeking) return true;
  return camera.readyState < HAVE_FUTURE_DATA && camera.mediaTime < camera.span - CLIP_TAIL_S;
}

export interface CameraStart {
  readonly playFrom: number | undefined;
  readonly currentTime: number;
  readonly paused: boolean;
  readonly ended: boolean;
  readonly failed: boolean;
}

export const isStarting = (camera: CameraStart): boolean =>
  camera.playFrom !== undefined && !camera.paused && !camera.ended && !camera.failed && camera.currentTime <= camera.playFrom;

export const isPlaybackRefused = (error: unknown): boolean => error instanceof DOMException && error.name === 'NotAllowedError';

export class StallGuard {
  private since: number | undefined;

  constructor(private readonly now: () => number = () => performance.now()) {}

  waiting(blocked: boolean): boolean {
    if (!blocked) {
      this.since = undefined;
      return false;
    }
    this.since ??= this.now();
    return this.now() - this.since < MAX_STALL_MS;
  }
}
